import { normalizeStoreStatus } from '~/utils/normalizeStoreStatus'
import { useRuntimeConfig } from '#imports'
import {
  StoreStatusSchema,
  StoreSameBrandStoresQuerySchema,
  StoreSameBrandStoresSuccessSchema,
  type SameBrandStores,
  type StoreMenusQuery
} from '~/services/api/generated'
import { useGuestToken } from '~/composables/useGuestToken'
import { isAvailabilityCode } from '~/composables/isAvailabilityCode'
import { availabilityResolverMap } from '~/composables/availabilityResolverMap'
import { resolveCode } from '~/composables/resolveCode'
import { computed, readonly, ref, watch, type Ref } from 'vue'
import type { TranslateFn } from '~/composables/TranslateFn'
import { useIdentity } from '~/composables/useIdentity'
import { useLogger } from '~/composables/useLogger'
import { useProblemPresentation } from '~/composables/useProblemPresentation'
import { useSearchApplication } from '~/composables/useSearchApplication'
import { useStoreOrderingContext } from '~/composables/useStoreOrderingContext'
import type { StoreOrderingContextInputs } from '~/composables/StoreOrderingContextInputs'
import { useLocalizedMessage } from '~/composables/useLocalizedMessage'
import { isOrderType } from '~/enums/order/isOrderType'
import { useStoreService } from '~/services/api/stores/useStoreService'
import { useMenusPublicService } from '~/services/api/menus/public/useMenusPublicService'
import { useCartActions } from '~/composables/useCartActions'
import type { CartItemInput } from '~/interfaces/CartItemInput'
import { useCartStore } from '~/stores/useCartStore'
import { useFiltersStore } from '~/stores/useFiltersStore'
import { mapPublicStoreMenuItem } from '~/composables/mapPublicStoreMenuItem'
import type {
  PublicStoreMenu,
  PublicStoreMenuCategory
} from '~/services/api/menus/public/useMenusPublicService'

const mapMenuCategory = (category: PublicStoreMenuCategory) => ({
  description: category.description,
  id: category.id,
  is_featured_category: category.is_featured_category,
  menu_items: category.menu_items.map(mapPublicStoreMenuItem),
  name: category.name,
  sort_order: category.sort_order,
  status: category.status
})
const mapMenu = (menu: PublicStoreMenu) => ({
  availability: menu.availability,
  availability_mode: menu.availability_mode,
  ...(isAvailabilityCode(menu.availability_code)
    ? { availability_code: menu.availability_code }
    : {}),
  categories: menu.categories.map(mapMenuCategory),
  custom_hours: menu.custom_hours,
  description: menu.description,
  id: menu.id,
  is_available: menu.is_available,
  name: menu.name,
  order_types: menu.order_types,
  schedule: menu.schedule,
  sort_order: menu.sort_order,
  status: menu.status,
  sync_with_store_hours: menu.sync_with_store_hours,
  tags: menu.tags,
  type: menu.type ?? '',
  types: menu.types
})

function useStorefrontLocations(inputs: {
  signal?: AbortSignal
  slug: Ref<string>
  logger: ReturnType<typeof useLogger>
  problemPresentation: ReturnType<typeof useProblemPresentation>
}) {
  const isCancelled = (): boolean => inputs.signal?.aborted === true
  const locationsData = ref<SameBrandStores | null>(null)
  const isFetchingLocations = ref(false)
  const fetchLocations = async (): Promise<void> => {
    if (isCancelled() || isFetchingLocations.value) return
    try {
      const currentSlug = inputs.slug.value
      if (currentSlug == null || currentSlug === '') return
      const baseUrl = useRuntimeConfig().public.apiBaseUrl
      if (!baseUrl) return
      isFetchingLocations.value = true
      const response = StoreSameBrandStoresSuccessSchema.parse(
        await $fetch('/stores/same-brand', {
          baseURL: `${baseUrl}/api`,
          query: StoreSameBrandStoresQuerySchema.parse({ slug: currentSlug })
        })
      )
      if (!isCancelled()) locationsData.value = response.data
    } catch (error) {
      if (isCancelled()) return
      const problem = inputs.problemPresentation.normalize(error)
      if (problem.status === 404) {
        locationsData.value = null
      } else if (problem.status !== 429) {
        inputs.logger.error('fetchLocations', { error })
      }
    } finally {
      if (!isCancelled()) isFetchingLocations.value = false
    }
  }
  return { locationsData: readonly(locationsData), fetchLocations }
}

function useStorefrontCartCommands(inputs: {
  cartActions: Pick<ReturnType<typeof useCartActions>, 'addItem' | 'updateItem'>
  ordering: ReturnType<typeof useStoreOrderingContext>
  logger: ReturnType<typeof useLogger>
  slug: Ref<string>
  t: TranslateFn
  orderDetailsMissingReason: Readonly<Ref<string>>
  selectedMenuUnavailableReason: Readonly<Ref<string>>
  selection: Readonly<Ref<{ readonly eventTime: string | null }>>
  storeSupportsOrderType: () => boolean
  onCloseMenuItemModal: (() => void) | undefined
}) {
  const {
    cartActions,
    ordering,
    logger,
    orderDetailsMissingReason,
    selectedMenuUnavailableReason,
    selection,
    storeSupportsOrderType
  } = inputs

  const isAddingToCart = ref(false)
  const checkCartAvailability = async (): Promise<boolean> => {
    if (orderDetailsMissingReason.value !== '') {
      ordering.showAvailabilityBlock(() => orderDetailsMissingReason.value)
      return false
    }
    const outcome = await ordering.refreshAvailability({ checkMenu: true })
    if (outcome.status === 'unavailable')
      ordering.showAvailabilityBlock(() => outcome.message, outcome.result.data)
    return outcome.status === 'available'
  }
  const addToCart = async (cartItem: CartItemInput): Promise<void> => {
    if (isAddingToCart.value) return
    if (!storeSupportsOrderType()) {
      ordering.showAvailabilityBlock(() => 'Not available for selected order type')
      return
    }
    if (ordering.isFullPageBlockVisible.value) {
      ordering.showAvailabilityBlock(() => ordering.closureMessage.value)
      inputs.onCloseMenuItemModal?.()
      return
    }
    logger.debug('addToCart', { cartItem })
    if (orderDetailsMissingReason.value !== '') {
      ordering.showAvailabilityBlock(() => orderDetailsMissingReason.value)
      return
    }
    const { getGuestToken } = useGuestToken()
    const storeSlug = inputs.slug.value
    if (storeSlug === '' || ordering.store.value === null) return
    const guestToken = getGuestToken()
    try {
      isAddingToCart.value = true
      const outcome = await ordering.refreshAvailability({ checkMenu: true })
      if (outcome.status === 'unavailable') {
        ordering.showAvailabilityBlock(() => outcome.message, outcome.result.data)
        return
      }
      if (outcome.status !== 'available') return
      if (selectedMenuUnavailableReason.value !== '') {
        ordering.showAvailabilityBlock(
          () => selectedMenuUnavailableReason.value,
          ordering.availabilityResult.value?.data ?? null
        )
        return
      }
      if (inputs.slug.value !== storeSlug) return
      const added = await cartActions.addItem(
        cartItem,
        storeSlug,
        guestToken ?? null,
        ordering.eventDate.value !== '' ? ordering.eventDate.value : null,
        selection.value.eventTime
      )
      if (added) inputs.onCloseMenuItemModal?.()
    } catch (error) {
      logger.error('addToCart', { error })
      ordering.showAvailabilityBlock(() => inputs.t('common.cart.addItemFailed'))
    } finally {
      isAddingToCart.value = false
    }
  }
  const updateCartItem = async (cartItemId: number, cartItem: CartItemInput): Promise<void> => {
    if (isAddingToCart.value) return
    if (!storeSupportsOrderType()) {
      ordering.showAvailabilityBlock(() => 'Not available for selected order type')
      return
    }
    const { getGuestToken } = useGuestToken()
    const storeSlug = inputs.slug.value
    if (storeSlug === '') return
    const guestToken = getGuestToken()
    try {
      isAddingToCart.value = true
      await cartActions.updateItem(cartItemId, cartItem, storeSlug, guestToken ?? null)
      inputs.onCloseMenuItemModal?.()
    } catch (error) {
      logger.error('updateCartItem', { error })
    } finally {
      isAddingToCart.value = false
    }
  }
  return {
    checkCartAvailability,
    addToCart,
    updateCartItem,
    isAddingToCart: computed(() => isAddingToCart.value)
  }
}

export function useStorefrontApplication(inputs: {
  signal?: AbortSignal
  slug: Ref<string>
  t: TranslateFn
  defaultClosedMessage: StoreOrderingContextInputs['defaultClosedMessage']
  onCloseMenuItemModal?: () => void
}) {
  const { searchStore, fetchAvailability } = useSearchApplication()
  const cartStore = useCartStore()
  const cartActions = useCartActions()
  const filtersStore = useFiltersStore()
  const { identity } = useIdentity()
  const storeService = useStoreService()
  const logger = useLogger('StorePage')
  const problemPresentation = useProblemPresentation()
  const { locationsData, fetchLocations } = useStorefrontLocations({
    ...(inputs.signal === undefined ? {} : { signal: inputs.signal }),
    slug: inputs.slug,
    logger,
    problemPresentation
  })
  const menusService = useMenusPublicService()
  const menus = ref<ReturnType<typeof mapMenu>[]>([])
  const selectedMenuId = ref<number | string | null>(null)
  const selectedMenuType = ref<'dine-in' | 'catering' | null>(null)
  const menusLoading = ref(true)
  const hasLoadedMenus = ref(false)
  const menusFetchStarted = ref(false)
  const menusError = useLocalizedMessage()
  const selection = computed(() => ({
    eventDate: searchStore.eventDateTime?.eventDate ?? null,
    eventTime: searchStore.eventDateTime?.eventTime ?? null,
    orderType: searchStore.orderType,
    guestCount: searchStore.guestCount,
    needsGuestCount: searchStore.needsGuestCount
  }))
  const orderDetailsMissingReason = computed(() => {
    const selected = selection.value
    if (
      selected.eventDate === null ||
      selected.eventDate === '' ||
      selected.eventTime === null ||
      selected.eventTime === ''
    ) {
      return 'Select a date and time before adding items.'
    }
    return selected.needsGuestCount
      ? inputs.t('commerce.storeSlug.addPartySizeBeforeAddingItems')
      : ''
  })
  const storeSupportsOrderType = (): boolean => {
    const orderTypes = ordering.store.value?.order_types
    return (
      Array.isArray(orderTypes) &&
      orderTypes.length > 0 &&
      orderTypes.every(isOrderType) &&
      orderTypes.includes(selection.value.orderType)
    )
  }
  const ordering = useStoreOrderingContext({
    coordinates: computed(() => searchStore.coordinates),
    defaultClosedMessage: inputs.defaultClosedMessage,
    eventTime: computed(() => selection.value.eventTime),
    fetchAvailability,
    fetchStore: storeService.getPublicStoreBySlug,
    guestCount: computed(() => selection.value.guestCount),
    onAvailabilityFetchError: (error) => {
      logger.error('refreshAvailability', { error })
    },
    onStoreFetchError: (error) => {
      logger.error('fetchStoreDetails', { error })
    },
    onTemporaryClosureDetected: () => {
      cartStore.closeStoreCart()
      inputs.onCloseMenuItemModal?.()
    },
    orderDetailsMissingReason,
    orderType: computed(() => selection.value.orderType),
    rawEventDate: computed(() => selection.value.eventDate),
    searchQuery: computed(() => searchStore.searchQuery),
    slug: inputs.slug,
    storeSupportsOrderType,
    t: inputs.t,
    userId: computed(() => identity.value?.id)
  })
  const normalizedStoreStatus = computed(() => normalizeStoreStatus(ordering.store.value?.status))
  const getStoreMenusQueryParams = (): Omit<StoreMenusQuery, 'slug'> => {
    const menuType = selectedMenuType.value
    if (menuType === null) {
      throw new TypeError('A supported order type is required to load store menus.')
    }
    return {
      menu_type: menuType,
      order_type: selection.value.orderType,
      ...(ordering.eventDate.value !== '' ? { requested_date: ordering.eventDate.value } : {}),
      ...(selection.value.eventTime !== null && selection.value.eventTime !== ''
        ? { requested_time: selection.value.eventTime }
        : {})
    }
  }
  const storeMenusRequestKey = computed(() =>
    selectedMenuType.value === null
      ? null
      : JSON.stringify({
          slug: inputs.slug.value,
          ...getStoreMenusQueryParams()
        })
  )
  const isInitializationCancelled = (): boolean => inputs.signal?.aborted === true
  const clearStoreMenusForUnsupportedOrderType = (): void => {
    if (isInitializationCancelled()) return
    menus.value = []
    selectedMenuId.value = null
    hasLoadedMenus.value = true
    menusLoading.value = false
    menusError.clear()
  }
  const applyStoreMenusResponse = (
    response: Awaited<ReturnType<typeof menusService.getStoreMenusBySlug>>,
    requestKey: string | null
  ): void => {
    if (isInitializationCancelled() || storeMenusRequestKey.value !== requestKey) return
    const projectedMenus = response.menus.map(mapMenu)
    menus.value = projectedMenus
    selectedMenuId.value = projectedMenus[0]?.id ?? null
    menusError.clear()
  }
  const applyStoreMenusFailure = (error: unknown, requestKey: string | null): void => {
    if (isInitializationCancelled() || storeMenusRequestKey.value !== requestKey) return
    logger.error('fetchStoreMenus', { error })
    menus.value = []
    selectedMenuId.value = null
    const problem = problemPresentation.normalize(error, { context: 'store page' })
    menusError.set(() =>
      problemPresentation.resolveMessage(
        problem,
        inputs.t,
        inputs.t('commerce.storeSlug.failedToLoadStoreMenus')
      )
    )
  }
  const finalizeStoreMenusFetch = (requestKey: string | null): void => {
    if (isInitializationCancelled() || storeMenusRequestKey.value !== requestKey) return
    hasLoadedMenus.value = true
    menusLoading.value = false
  }
  const fetchStoreMenus = async (): Promise<void> => {
    if (isInitializationCancelled()) return
    menusFetchStarted.value = true
    if (selectedMenuType.value === null || !storeSupportsOrderType()) {
      clearStoreMenusForUnsupportedOrderType()
      return
    }
    const requestKey = storeMenusRequestKey.value
    try {
      menusLoading.value = true
      const currentSlug = inputs.slug.value
      if (currentSlug === '') throw new Error('Store slug is required')
      const response = await menusService.getStoreMenusBySlug({
        slug: currentSlug,
        ...getStoreMenusQueryParams()
      })
      applyStoreMenusResponse(response, requestKey)
    } catch (error: unknown) {
      applyStoreMenusFailure(error, requestKey)
    } finally {
      finalizeStoreMenusFetch(requestKey)
    }
  }
  watch(
    storeMenusRequestKey,
    async () => {
      if (
        !menusFetchStarted.value ||
        inputs.slug.value === '' ||
        storeMenusRequestKey.value === null
      )
        return
      await fetchStoreMenus()
    },
    { flush: 'post' }
  )
  const restoreContext = (): void => {
    filtersStore.restoreFiltersFromCookie()
    searchStore.restoreSearchFromCookie()
    selectedMenuType.value = selection.value.orderType === 'dine_in' ? 'dine-in' : 'catering'
    if (inputs.slug.value !== '') cartStore.setCurrentStore(inputs.slug.value)
  }
  const selectedMenu = computed(() => {
    const id = selectedMenuId.value
    if (id === null || id === '') return menus.value[0] ?? null
    return menus.value.find((menu) => String(menu.id) === String(id)) ?? menus.value[0] ?? null
  })
  const hasMenuDateValidationContext = computed(
    () => selection.value.eventDate !== null && selection.value.eventDate !== ''
  )
  const getMenuUnavailableReason = (
    menu: ReturnType<typeof mapMenu> | null | undefined
  ): string => {
    if (menu === null || menu === undefined) return ''
    if (!storeSupportsOrderType()) return 'Not available for selected order type'
    if (!hasMenuDateValidationContext.value) return ''
    const storeReason = ordering.getFirstAvailabilityReason()
    if (storeReason !== '') return storeReason
    if (normalizedStoreStatus.value === StoreStatusSchema.enum.disabled)
      return 'Store closed at selected time'
    if (menu.status.toLowerCase() !== 'active' && menu.status !== 'published')
      return 'Not available at this time'
    if (!menu.is_available) {
      if (menu.availability_code !== undefined && isAvailabilityCode(menu.availability_code)) {
        return resolveCode({ code: menu.availability_code }, availabilityResolverMap, inputs.t)
      }
      return selection.value.eventTime !== null && selection.value.eventTime !== ''
        ? 'Unavailable at Selected Time'
        : 'Unavailable on Selected Date'
    }
    return ''
  }
  const selectedMenuUnavailableReason = computed(() => getMenuUnavailableReason(selectedMenu.value))
  const { checkCartAvailability, addToCart, updateCartItem, isAddingToCart } =
    useStorefrontCartCommands({
      cartActions,
      ordering,
      logger,
      slug: inputs.slug,
      t: inputs.t,
      orderDetailsMissingReason,
      selectedMenuUnavailableReason,
      selection,
      storeSupportsOrderType,
      onCloseMenuItemModal: inputs.onCloseMenuItemModal
    })
  return {
    ...ordering,
    locationsData,
    fetchLocations,
    menus: computed(() => menus.value),
    selectedMenuId,
    selectedMenuType,
    menusLoading,
    hasLoadedMenus,
    menusError: menusError.message,
    fetchStoreMenus,
    selection: readonly(selection),
    orderDetailsMissingReason,
    storeSupportsOrderType,
    restoreContext,
    setOrderType: searchStore.setOrderType,
    selectedMenu,
    hasMenuDateValidationContext,
    getMenuUnavailableReason,
    selectedMenuUnavailableReason,
    checkCartAvailability,
    addToCart,
    updateCartItem,
    isAddingToCart,
    normalizedStoreStatus
  }
}
